import { ComputedRef, InjectionKey, computed, inject, provide } from 'vue';
import {
  WidgetChartType,
  type WidgetSettingsWindowData,
} from '@/dashboard/shared/types/widgets.types';

// Context type with data + update methods
export interface WidgetSettingContext<T extends WidgetChartType = WidgetChartType> {
  data: WidgetSettingsWindowData<T>;
  chartOption: ComputedRef<WidgetSettingsWindowData<T>['chartOption']>;
  updateChartOption: <K extends keyof WidgetSettingsWindowData<T>['chartOption']>(
    key: K,
    value: WidgetSettingsWindowData<T>['chartOption'][K],
  ) => void;
  updateChartOptionPartial: (updates: Partial<WidgetSettingsWindowData<T>['chartOption']>) => void;
}

// check if the value is a plain object
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

// Recursively merges source keys into target in-place (mutates target directly).
export const deepMergeInPlace = (
  target: Record<string, unknown>,
  source: Record<string, unknown>,
) => {
  for (const k of Object.keys(source)) {
    const src = source[k];
    const tgt = target[k];
    if (src === undefined) {
      delete target[k];
    } else if (Array.isArray(src)) {
      target[k] = src;
    } else if (isPlainObject(src) && isPlainObject(tgt)) {
      deepMergeInPlace(tgt, src);
    } else {
      target[k] = src;
    }
  }
};

const key = Symbol('WidgetSettingData') as InjectionKey<WidgetSettingContext>;

export const provideWidgetSettingData = <T extends WidgetChartType>(
  data: WidgetSettingsWindowData<T>,
): WidgetSettingContext<T> => {
  /**
   * Updates a single top-level key in chartOption.
   */
  const updateChartOption = <K extends keyof WidgetSettingsWindowData<T>['chartOption']>(
    optionKey: K,
    value: WidgetSettingsWindowData<T>['chartOption'][K],
  ) => {
    (data.chartOption as WidgetSettingsWindowData<T>['chartOption'])[optionKey] = value;
  };

  /**
   * Partially updates chartOption using deepMergeInPlace.
   * - Nested plain objects are recursively merged, not replaced.
   * - Arrays are replaced wholesale.
   * - undefined values delete the corresponding key (including nested).
   *
   * @example
   * // Given: chartOption.tree = { parent: { id: 'a', sort: 'x' }, child: { id: 'b' } }
   * updateChartOptionPartial({ tree: { parent: { id: 'new-a' } } });
   * // Result: { parent: { id: 'new-a', sort: 'x' }, child: { id: 'b' } }
   */
  const updateChartOptionPartial = (
    updates: Partial<WidgetSettingsWindowData<T>['chartOption']>,
  ) => {
    deepMergeInPlace(
      data.chartOption as Record<string, unknown>,
      updates as Record<string, unknown>,
    );
  };

  const chartOption = computed(() => data.chartOption);

  const context: WidgetSettingContext<T> = {
    data,
    chartOption,
    updateChartOption,
    updateChartOptionPartial,
  };

  provide(key, context as WidgetSettingContext);
  return context;
};

/**
 * Returns the full context including data and update methods.
 * Use this when you need to update chartOption.
 */
export const useWidgetSettingContext = <T extends WidgetChartType>(): WidgetSettingContext<T> => {
  const context = inject(key) as WidgetSettingContext<T> | undefined;

  if (!context) {
    throw new Error('useWidgetSettingContext must be used within a WidgetSettingDataProvider');
  }

  return context;
};

/**
 * Returns only the widget settings data (backward-compatible).
 * For new code that needs to update chartOption, use useWidgetSettingContext instead.
 */
export const useWidgetSettingData = <T extends WidgetChartType>(): WidgetSettingsWindowData<T> => {
  const context = inject(key) as WidgetSettingContext<T> | undefined;

  if (!context) {
    throw new Error('useWidgetSettingData must be used within a WidgetSettingDataProvider');
  }

  return context.data;
};

/**
 * Safe version of useWidgetSettingData — returns undefined when called outside a provider.
 * Use this in composables that may be used in contexts without the widget setting provider.
 */
export const tryUseWidgetSettingData = <T extends WidgetChartType>():
  | WidgetSettingsWindowData<T>
  | undefined => {
  const context = inject<WidgetSettingContext<T> | undefined>(
    key as InjectionKey<WidgetSettingContext<T> | undefined>,
    undefined,
  );
  return context?.data;
};
